'use client';

import { useEffect, useState } from 'react';


/**
 * A moment, on two lines: the date large, the time small underneath.
 *
 * Every table in both apps used to print `new Date(x).toLocaleString()` on
 * its own, which is a locale-dependent single string (`8/11/2026, 11:17:55
 * AM` in one browser, something else shaped in another), squeezed into
 * whatever column width happened to be left. One component, used everywhere
 * a full timestamp (date and time both) is shown in a table or a detail
 * view, so the shape is decided once.
 *
 * Local time, same as the single-string version this replaces; only the
 * layout changed, not the timezone.
 */
/**
 * Whether the browser has taken over from the server's HTML.
 *
 * The server formats in its own timezone (UTC on the box) and the reader's
 * browser in theirs, so a timestamp rendered straight into the first paint is
 * a hydration mismatch on every row of every table — React throws #418 and
 * rebuilds the tree. So the first client render repeats what the server sent,
 * and local time arrives one tick later, when it can no longer disagree with
 * anything.
 */
function useHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  return hydrated;
}

export function Timestamp({
  value,
  inline = false,
}: {
  value: string | Date | null | undefined;
  /** Day and time on one line, for a table whose rows are read down rather
   *  than across: two lines per row is twice the scrolling for the same log. */
  inline?: boolean;
}) {
  const hydrated = useHydrated();
  if (value === null || value === undefined) return <>—</>;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return <>—</>;

  // UTC until the browser is in charge, then the reader's own clock.
  const timeZone = hydrated ? undefined : 'UTC';

  // sv-SE and en-GB are not about Sweden or Britain: they are the two
  // locales `Intl` happens to format as plain `YYYY-MM-DD` and 24-hour
  // `HH:MM:SS` with no further formatting quirks, which is what every
  // reader of a log or a table wants regardless of where they are.
  const day = date.toLocaleDateString('sv-SE', { timeZone });
  const time = date.toLocaleTimeString('en-GB', { timeZone });

  return (
    <span className={inline ? 'stamp is-inline' : 'stamp'}>
      <span>{day}</span>
      <span className="stamp-time">{time}</span>
    </span>
  );
}

// The text helpers live in `packages/shared/src/when.ts` and are re-exported
// here so every call site keeps its import. A re-export is not a copy.
export { agoShort, duration, elapsed, whenNear } from '@content-removal/shared';
